import React, { useEffect, useState } from 'react';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Text,
  BlockStack,
  Spinner,
  Button,
  Select,
  Banner,
  InlineStack,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';

interface SupportTicket {
  id: string;
  shop_id: string;
  shop_domain: string;
  subject: string;
  message: string;
  priority: 'high' | 'normal';
  status: 'open' | 'closed' | 'in_progress';
  created_at: string;
  updated_at: string;
}

const AdminSupportTickets: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    closed: 0,
    highPriority: 0,
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchTickets();
  }, [navigate, statusFilter, priorityFilter]);

  const fetchTickets = async () => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      const token = localStorage.getItem('admin_token');

      if (!token) {
        navigate('/admin/login');
        return;
      }

      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);

      const response = await fetch(`${supabaseUrl}/functions/v1/admin-support-tickets?${params.toString()}`, {
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'X-Admin-Token': token,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
        setStats(data.stats || { total: 0, open: 0, closed: 0, highPriority: 0 });
      } else if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (ticketId: string, newStatus: 'open' | 'closed' | 'in_progress') => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      const token = localStorage.getItem('admin_token');

      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/admin-support-tickets`, {
        method: 'PATCH',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'X-Admin-Token': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ticketId,
          status: newStatus,
        }),
      });

      if (response.ok) {
        await fetchTickets();
      }
    } catch (error) {
      console.error('Error updating ticket status:', error);
    }
  };

  const getStatusBadge = (status: string): React.ReactNode => {
    switch (status) {
      case 'open':
        return <Badge tone="critical">Aberto</Badge>;
      case 'in_progress':
        return <Badge tone="info">Em Andamento</Badge>;
      case 'closed':
        return <Badge tone="success">Fechado</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority: string): React.ReactNode => {
    if (priority === 'high') {
      return (
        <Badge tone="critical" size="large">
          🔥 ALTA PRIORIDADE (PRO)
        </Badge>
      );
    }
    return <Badge>Normal</Badge>;
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Page>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  // Ordenar: PRO primeiro (high priority)
  const sortedTickets = [...tickets].sort((a, b) => {
    if (a.priority === 'high' && b.priority !== 'high') return -1;
    if (a.priority !== 'high' && b.priority === 'high') return 1;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const sortedRows = sortedTickets.map((ticket) => [
    ticket.shop_domain,
    getPriorityBadge(ticket.priority),
    ticket.subject,
    ticket.message.length > 100 ? ticket.message.substring(0, 100) + '...' : ticket.message,
    getStatusBadge(ticket.status),
    formatDate(ticket.created_at),
    <InlineStack key={ticket.id} gap="200">
      {ticket.status !== 'closed' && (
        <Button
          size="micro"
          onClick={() => handleStatusChange(ticket.id, ticket.status === 'open' ? 'in_progress' : 'closed')}
        >
          {ticket.status === 'open' ? 'Marcar como Em Andamento' : 'Fechar'}
        </Button>
      )}
      {ticket.status === 'closed' && (
        <Button size="micro" onClick={() => handleStatusChange(ticket.id, 'open')}>
          Reabrir
        </Button>
      )}
    </InlineStack>,
  ]);

  return (
    <Page
      title="Tickets de Suporte"
      primaryAction={{
        content: 'Voltar ao Dashboard',
        onAction: () => navigate('/admin/dashboard'),
      }}
      secondaryActions={[
        {
          content: 'Atualizar',
          onAction: fetchTickets,
        },
        {
          content: 'Sair',
          onAction: () => {
            localStorage.removeItem('admin_token');
            navigate('/admin/login');
          },
        },
      ]}
    >
      <BlockStack gap="500">
        {/* Estatísticas */}
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Estatísticas
            </Text>
            <InlineStack gap="500">
              <div>
                <Text as="p" variant="bodySm" tone="subdued">Total</Text>
                <Text as="p" variant="headingLg">{stats.total}</Text>
              </div>
              <div>
                <Text as="p" variant="bodySm" tone="subdued">Abertos</Text>
                <Text as="p" variant="headingLg" tone="critical">{stats.open}</Text>
              </div>
              <div>
                <Text as="p" variant="bodySm" tone="subdued">Fechados</Text>
                <Text as="p" variant="headingLg" tone="success">{stats.closed}</Text>
              </div>
              <div>
                <Text as="p" variant="bodySm" tone="subdued">🔥 Alta Prioridade (PRO)</Text>
                <Text as="p" variant="headingLg" tone="critical">{stats.highPriority}</Text>
              </div>
            </InlineStack>
          </BlockStack>
        </Card>

        {/* Filtros */}
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Filtros
            </Text>
            <InlineStack gap="300">
              <div style={{ minWidth: '200px' }}>
                <Select
                  label="Status"
                  options={[
                    { label: 'Todos', value: 'all' },
                    { label: 'Aberto', value: 'open' },
                    { label: 'Em Andamento', value: 'in_progress' },
                    { label: 'Fechado', value: 'closed' },
                  ]}
                  value={statusFilter}
                  onChange={setStatusFilter}
                />
              </div>
              <div style={{ minWidth: '200px' }}>
                <Select
                  label="Prioridade"
                  options={[
                    { label: 'Todas', value: 'all' },
                    { label: '🔥 Alta (PRO)', value: 'high' },
                    { label: 'Normal', value: 'normal' },
                  ]}
                  value={priorityFilter}
                  onChange={setPriorityFilter}
                />
              </div>
            </InlineStack>
          </BlockStack>
        </Card>

        {/* Alerta de PRO */}
        {stats.highPriority > 0 && (
          <Banner tone="critical">
            <Text as="p" variant="bodySm" fontWeight="semibold">
              ⚠️ {stats.highPriority} {stats.highPriority === 1 ? 'ticket' : 'tickets'} de clientes PRO aguardando resposta!
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Tickets de clientes PRO têm prioridade alta e devem ser respondidos em até 24 horas.
            </Text>
          </Banner>
        )}

        {/* Tabela */}
        <Card>
          <BlockStack gap="400">
            <DataTable
              columnContentTypes={[
                'text',
                'text',
                'text',
                'text',
                'text',
                'text',
                'text',
              ]}
              headings={[
                'Loja',
                'Prioridade',
                'Assunto',
                'Mensagem',
                'Status',
                'Data',
                'Ações',
              ]}
              rows={sortedRows}
            />
          </BlockStack>
        </Card>

        {tickets.length === 0 && (
          <Card>
            <BlockStack gap="300" align="center">
              <Text as="p" tone="subdued">
                Nenhum ticket encontrado.
              </Text>
            </BlockStack>
          </Card>
        )}
      </BlockStack>
    </Page>
  );
};

export default AdminSupportTickets;

